import "./pages.css";
import React, { useEffect, useState, useRef, useCallback } from "react";
import Linkify from "linkify-react";
import { useCart } from "@/contexts/CartContext";
import { CheckoutDialog } from "@/components/CheckoutDialog";
import PhoneInput from "react-phone-number-input";

interface Block {
    id: string;
    type: "text" | "image" | "button" | "input" | "html" | "container";
    data?: {
        content?: string;
        textAlign?: "left" | "center" | "right" | "justify";
        marginTop?: string;
        marginBottom?: string;
        marginLeft?: string;
        marginRight?: string;
        fontSize?: string;
        fontWeight?: "normal" | "bold";
        fontFamily?: string;
        color?: string;
        url?: string;
        mediaType?: "image" | "video";
        aspectRatio?: "1:1" | "16:9" | "original";
        maxWidth?: string;
        width?: string;
        borderRadius?: string;
        text?: string;
        buttonUrl?: string;
        backgroundColor?: string;
        textColor?: string;
        buttonText?: string;
        dialogTitle?: string;
        confirmationMessage?: string;
        collectName?: boolean;
        collectPhone?: boolean;
        collectEmail?: boolean;
        inputType?: "email" | "phone" | "text" | "select";
        label?: string;
        placeholder?: string;
        options?: Array<{ value: string; label: string }>;
        html?: string;
        columns?: Block[][];
        numColumns?: number;
        paddingTop?: string;
        paddingBottom?: string;
        paddingLeft?: string;
        paddingRight?: string;
        onClick?: Array<{ type: "ViewContent" } | { type: "AddToCart"; productId: string } | { type: "InitiateCheckout" } | { type: "OpenURL"; url: string } | { type: "Submit" }>;
        conditionalRendering?: { watchInputBlockId: string; watchValue: string };
    };
    blocks?: Block[];
}

interface Page {
    pageTitle?: string;
    metaImageUrl?: string;
    blocks?: Block[];
    pageBackgroundColor?: string;
    backgroundImageUrl?: string;
    pageFontFamily?: string;
    pageTextColor?: string;
    onSubmit?: Array<{ type: string; pageId?: string }>;
}

interface PageBlockRendererProps {
    page: Page;
}

export function PageBlockRenderer({ page }: PageBlockRendererProps) {
    const { addToCart } = useCart();
    const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const blockRefs = useRef<Record<string, HTMLElement>>({});

    const handleInitiateCheckout = () => {
        setCheckoutDialogOpen(true);
    };

    const collectInputBlocks = (blocks: Block[]): Block[] => {
        const inputBlocks: Block[] = [];

        for (const block of blocks) {
            if (block.type === "input") {
                inputBlocks.push(block);
            }

            if (block.type === "container") {
                const columns = Array.isArray(block.data?.columns) ? block.data.columns : null;
                if (columns) {
                    for (const column of columns) {
                        inputBlocks.push(...collectInputBlocks(column));
                    }
                }
            }

            if (block.blocks) {
                inputBlocks.push(...collectInputBlocks(block.blocks));
            }
        }

        return inputBlocks;
    };

    const handleSubmit = useCallback(async () => {
        if (!page || isSubmitting) return;

        const allInputBlocks = collectInputBlocks(page.blocks || []);

        const responses = allInputBlocks
            .map((b: Block) => {
                const element = document.getElementById(`input-${b.id}`) as HTMLInputElement | HTMLSelectElement | null;
                let value = "";

                if (element) {
                    if (b.data?.inputType === "phone") {
                        const phoneInput = element.querySelector('input[type="tel"]') as HTMLInputElement;
                        value = phoneInput?.value || "";
                    } else {
                        value = element.value || "";
                    }
                }

                return {
                    block: b.id,
                    value: value,
                };
            })
            .filter((r) => r.value);

        setIsSubmitting(true);
        try {
            const currentPath = window.location.pathname;
            const response = await fetch(`/api${currentPath}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ responses }),
            });

            if (response.ok) {
                const data = await response.json();

                allInputBlocks.forEach((b: Block) => {
                    const element = document.getElementById(`input-${b.id}`) as HTMLInputElement | HTMLSelectElement | null;
                    if (element) {
                        if (b.data?.inputType === "phone") {
                            const phoneInput = element.querySelector('input[type="tel"]') as HTMLInputElement;
                            if (phoneInput) phoneInput.value = "";
                        } else {
                            element.value = "";
                        }
                    }
                });

                if (data.redirectUri) {
                    window.location.href = data.redirectUri;
                    return;
                }

                if (page.onSubmit && Array.isArray(page.onSubmit)) {
                    const goToAction = page.onSubmit.find((action: any) => action.type === "GoTo" && action.pageId);
                    if (goToAction && goToAction.pageId && data.redirectTo) {
                        window.location.href = data.redirectTo;
                        return;
                    }
                }
            } else {
                console.error("Failed to submit responses:", await response.text());
            }
        } catch (error) {
            console.error("Error submitting responses:", error);
        } finally {
            setIsSubmitting(false);
        }
    }, [page, isSubmitting]);

    const darkenColor = (hex: string | undefined, amount: number = 20): string => {
        if (!hex) return "#1d4ed8";
        const rgb = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!rgb || !rgb[1] || !rgb[2] || !rgb[3]) return hex;
        const r = Math.max(0, parseInt(rgb[1], 16) - amount);
        const g = Math.max(0, parseInt(rgb[2], 16) - amount);
        const b = Math.max(0, parseInt(rgb[3], 16) - amount);
        return `rgb(${r}, ${g}, ${b})`;
    };

    const handleBlockClick = (block: Block) => {
        if (isSubmitting) return;

        if (block.data?.onClick && block.data.onClick.length > 0) {
            block.data.onClick.forEach((action) => {
                if (action.type === "AddToCart" && action.productId) {
                    addToCart(action.productId);
                } else if (action.type === "InitiateCheckout") {
                    handleInitiateCheckout();
                } else if (action.type === "Submit") {
                    handleSubmit();
                }
            });
        }
    };

    const TextBlockComponent = ({ block }: { block: Block }) => {
        const content = block.data?.content?.trim() || "";
        if (!content) return null;

        const marginStyle: React.CSSProperties = {
            marginTop: block.data?.marginTop ? `${block.data.marginTop}px` : undefined,
            marginBottom: block.data?.marginBottom ? `${block.data.marginBottom}px` : undefined,
            marginLeft: block.data?.marginLeft ? `${block.data.marginLeft}px` : undefined,
            marginRight: block.data?.marginRight ? `${block.data.marginRight}px` : undefined,
        };

        const textStyle: React.CSSProperties = {
            textAlign: block.data?.textAlign || "left",
            fontSize: block.data?.fontSize ? `${block.data.fontSize}px` : undefined,
            fontWeight: block.data?.fontWeight === "bold" ? "bold" : block.data?.fontWeight === "normal" ? "normal" : undefined,
            fontFamily: block.data?.fontFamily || undefined,
            color: block.data?.color || undefined,
        };

        const hasOnClick = block.data?.onClick && block.data.onClick.length > 0;
        const linkifyOptions = {
            target: "_blank",
            rel: "noopener noreferrer",
        };
        const contentWithBreaks = content.split('\n').map((line, i, arr) => (
            <React.Fragment key={i}>
                <Linkify as="span" options={linkifyOptions}>{line}</Linkify>
                {i < arr.length - 1 && <br />}
            </React.Fragment>
        ));

        return (
            <div style={marginStyle} onClick={hasOnClick ? () => handleBlockClick(block) : undefined}>
                <p style={textStyle}>{contentWithBreaks}</p>
            </div>
        );
    };

    const MediaBlockComponent = ({ block }: { block: Block }) => {
        const url = block.data?.url?.trim() || "";
        const mediaType = block.data?.mediaType || "image";
        const aspectRatio = block.data?.aspectRatio || "16:9";
        const maxWidth = block.data?.maxWidth?.trim();
        const useOriginalAspect = aspectRatio === "original";
        const aspectRatioValue = aspectRatio === "1:1" ? "1 / 1" : "16 / 9";

        const marginStyle: React.CSSProperties = {
            marginTop: block.data?.marginTop ? `${block.data.marginTop}px` : undefined,
            marginBottom: block.data?.marginBottom ? `${block.data.marginBottom}px` : undefined,
            marginLeft: block.data?.marginLeft ? `${block.data.marginLeft}px` : undefined,
            marginRight: block.data?.marginRight ? `${block.data.marginRight}px` : undefined,
        };

        const containerStyle: React.CSSProperties = {
            width: "100%",
            aspectRatio: useOriginalAspect ? undefined : aspectRatioValue,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            maxWidth: maxWidth ? `${maxWidth}px` : undefined,
            margin: maxWidth ? "0 auto" : undefined,
        };

        const mediaStyle: React.CSSProperties = useOriginalAspect
            ? { maxWidth: "100%", height: "auto" }
            : { width: "100%", height: "100%", objectFit: "cover" };

        const hasOnClick = block.data?.onClick && block.data.onClick.length > 0;

        return (
            <div style={marginStyle} onClick={hasOnClick ? () => handleBlockClick(block) : undefined}>
                <div style={containerStyle}>
                    {url ? (
                        mediaType === "video" ? (
                            <video
                                src={url}
                                controls
                                style={mediaStyle}
                                onError={(e) => {
                                    (e.target as HTMLVideoElement).style.display = "none";
                                }}
                            />
                        ) : (
                            <img
                                src={url}
                                alt=""
                                style={mediaStyle}
                                onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = "none";
                                }}
                            />
                        )
                    ) : (
                        <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>No {mediaType}</div>
                    )}
                </div>
            </div>
        );
    };

    const ButtonBlockComponent = ({ block }: { block: Block }) => {
        const text = block.data?.text?.trim() || "Button";
        const onClickActions = block.data?.onClick || [];
        const openUrlAction = onClickActions.find((action: any) => action.type === "OpenURL");
        const buttonUrl = openUrlAction?.url || "#";
        const bgColor = block.data?.backgroundColor || "#2563eb";
        const txtColor = block.data?.textColor || "#ffffff";
        const hoverColor = darkenColor(block.data?.backgroundColor);

        const marginStyle: React.CSSProperties = {
            marginTop: block.data?.marginTop ? `${block.data.marginTop}px` : undefined,
            marginBottom: block.data?.marginBottom ? `${block.data.marginBottom}px` : undefined,
            marginLeft: block.data?.marginLeft ? `${block.data.marginLeft}px` : undefined,
            marginRight: block.data?.marginRight ? `${block.data.marginRight}px` : undefined,
        };

        const fontWeight = block.data?.fontWeight;
        const fontSize = block.data?.fontSize;
        const padTop = block.data?.paddingTop !== undefined ? block.data.paddingTop : "8";
        const padBottom = block.data?.paddingBottom !== undefined ? block.data.paddingBottom : "8";
        const padLeft = block.data?.paddingLeft !== undefined ? block.data.paddingLeft : "12";
        const padRight = block.data?.paddingRight !== undefined ? block.data.paddingRight : "12";
        const buttonStyle: React.CSSProperties = {
            display: "inline-block",
            paddingTop: `${padTop}px`,
            paddingBottom: `${padBottom}px`,
            paddingLeft: `${padLeft}px`,
            paddingRight: `${padRight}px`,
            backgroundColor: bgColor,
            color: txtColor,
            textDecoration: "none",
            borderRadius: block.data?.borderRadius ? `${block.data.borderRadius}px` : undefined,
            width: block.data?.width || undefined,
            fontSize: fontSize ? `${fontSize}px` : undefined,
            fontWeight: fontWeight === "bold" ? "bold" : fontWeight === "normal" ? "normal" : undefined,
            transition: "background-color 0.2s",
            opacity: isSubmitting ? 0.6 : 1,
            cursor: isSubmitting ? "not-allowed" : "pointer",
            pointerEvents: isSubmitting ? "none" : "auto",
        };

        const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
            if (isSubmitting) {
                e.preventDefault();
                return;
            }
            handleBlockClick(block);
        };

        const handleMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
            if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = hoverColor;
            }
        };

        const handleMouseLeave = (e: React.MouseEvent<HTMLAnchorElement>) => {
            if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = bgColor;
            }
        };

        const textAlign = block.data?.textAlign || "center";

        return (
            <div style={marginStyle}>
                <div style={{ textAlign: textAlign as "left" | "center" | "right" | "justify" }}>
                    <a
                        href={buttonUrl}
                        style={buttonStyle}
                        onClick={handleClick}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        {text}
                    </a>
                </div>
            </div>
        );
    };

    const HTMLBlockComponent = ({ block }: { block: Block }) => {
        const ref = useRef<HTMLDivElement>(null);

        useEffect(() => {
            if (!ref.current || !block.data?.html) return;

            ref.current.innerHTML = block.data.html;

            const scripts = ref.current.querySelectorAll("script");

            scripts.forEach((oldScript) => {
                const newScript = document.createElement("script");

                Array.from(oldScript.attributes).forEach((attr) => {
                    newScript.setAttribute(attr.name, attr.value);
                });

                newScript.appendChild(document.createTextNode(oldScript.innerHTML));

                oldScript.parentNode?.replaceChild(newScript, oldScript);
            });
        }, [block.data?.html]);

        const marginStyle: React.CSSProperties = {
            marginTop: block.data?.marginTop ? `${block.data.marginTop}px` : undefined,
            marginBottom: block.data?.marginBottom ? `${block.data.marginBottom}px` : undefined,
            marginLeft: block.data?.marginLeft ? `${block.data.marginLeft}px` : undefined,
            marginRight: block.data?.marginRight ? `${block.data.marginRight}px` : undefined,
        };

        const hasOnClick = block.data?.onClick && block.data.onClick.length > 0;

        return (
            <div
                ref={ref}
                style={marginStyle}
                onClick={hasOnClick ? () => handleBlockClick(block) : undefined}
            />
        );
    };

    const InputBlockComponent = ({ block }: { block: Block }) => {
        const inputType = block.data?.inputType || "text";
        const label = block.data?.label?.trim() || "";
        const placeholder = block.data?.placeholder?.trim() || "";
        const options = block.data?.options || [];
        const inputId = `input-${block.id}`;
        const borderRadius = block.data?.borderRadius ? `${block.data.borderRadius}px` : undefined;
        const phoneInputRef = useRef<HTMLDivElement>(null);

        useEffect(() => {
            if (inputType === "phone" && phoneInputRef.current && borderRadius) {
                const input = phoneInputRef.current.querySelector('input[type="tel"]') as HTMLInputElement;
                if (input) {
                    input.style.borderRadius = borderRadius;
                }
            }
        }, [inputType, borderRadius]);

        const marginStyle: React.CSSProperties = {
            marginTop: block.data?.marginTop ? `${block.data.marginTop}px` : undefined,
            marginBottom: block.data?.marginBottom ? `${block.data.marginBottom}px` : undefined,
            marginLeft: block.data?.marginLeft ? `${block.data.marginLeft}px` : undefined,
            marginRight: block.data?.marginRight ? `${block.data.marginRight}px` : undefined,
        };

        const handleInputChange = () => {
            updateConditionalRendering();
        };

        return (
            <div style={marginStyle}>
                <div className="w-full space-y-2">
                    {label && (
                        <label className="block" htmlFor={inputId}>
                            {label}
                        </label>
                    )}
                    {inputType === "phone" ? (
                        <div id={inputId} ref={phoneInputRef}>
                            <PhoneInput
                                defaultCountry="US"
                                international={false}
                                placeholder={placeholder}
                                disabled={isSubmitting}
                                className="w-full [&_.PhoneInputInput]:px-3 [&_.PhoneInputInput]:py-2 [&_.PhoneInputInput]:border [&_.PhoneInputInput]:border-gray-300 [&_.PhoneInputInput]:placeholder:text-gray-400 [&_.PhoneInputInput]:w-full [&_.PhoneInputInput]:outline-none disabled:[&_.PhoneInputInput]:opacity-60 disabled:[&_.PhoneInputInput]:cursor-not-allowed"
                                onChange={handleInputChange}
                            />
                        </div>
                    ) : inputType === "select" ? (
                        <select
                            id={inputId}
                            disabled={isSubmitting}
                            className="w-full px-3 py-2 border border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed [&>option[value='']]:text-gray-400"
                            style={{
                                borderRadius: borderRadius,
                            }}
                            defaultValue=""
                            onChange={handleInputChange}
                        >
                            {placeholder && (
                                <option value="" disabled style={{ color: '#9ca3af' }}>
                                    {placeholder}
                                </option>
                            )}
                            {options.map((option, index) => (
                                <option key={index} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <input
                            id={inputId}
                            type={inputType}
                            placeholder={placeholder}
                            disabled={isSubmitting}
                            className="w-full px-3 py-2 border border-gray-300 placeholder:text-gray-400 disabled:opacity-60 disabled:cursor-not-allowed"
                            style={{
                                borderRadius: borderRadius,
                            }}
                            defaultValue=""
                            onChange={handleInputChange}
                        />
                    )}
                </div>
            </div>
        );
    };

    const ContainerBlockComponent = ({ block }: { block: Block }) => {
        const columns: Block[][] = Array.isArray(block.data?.columns) ? block.data.columns : [[]];
        const columnCount = block.data?.numColumns || columns.length || 1;

        const wrapperStyle: React.CSSProperties = {
            width: block.data?.width || undefined,
            maxWidth: block.data?.maxWidth || undefined,
            marginLeft: "auto",
            marginRight: "auto",
        };

        const innerStyle: React.CSSProperties = {
            marginTop: block.data?.marginTop ? `${block.data.marginTop}px` : undefined,
            marginBottom: block.data?.marginBottom ? `${block.data.marginBottom}px` : undefined,
            marginLeft: block.data?.marginLeft ? `${block.data.marginLeft}px` : undefined,
            marginRight: block.data?.marginRight ? `${block.data.marginRight}px` : undefined,
            backgroundColor: block.data?.backgroundColor || undefined,
            borderRadius: block.data?.borderRadius ? `${block.data.borderRadius}px` : undefined,
            display: "grid",
            gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
        };

        return (
            <div style={wrapperStyle}>
                <div style={innerStyle}>
                    {columns.map((columnBlocks, colIndex) => (
                        <div key={colIndex} style={{ display: "flex", flexDirection: "column" }}>
                            {columnBlocks.map((nestedBlock) => (
                                <BlockRenderer key={nestedBlock.id} block={nestedBlock} />
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const getInputValue = (blockId: string): string => {
        const element = document.getElementById(`input-${blockId}`) as HTMLInputElement | HTMLSelectElement | null;
        if (!element) return "";

        const phoneInput = element.querySelector('input[type="tel"]') as HTMLInputElement;
        if (phoneInput) {
            return phoneInput.value || "";
        }

        return element.value || "";
    };

    const collectBlocksWithConditionalRendering = (blocks: Block[]): Block[] => {
        const blocksWithConditional: Block[] = [];

        for (const block of blocks) {
            if (block.data?.conditionalRendering) {
                blocksWithConditional.push(block);
            }

            if (block.type === "container") {
                const columns = Array.isArray(block.data?.columns) ? block.data.columns : null;
                if (columns) {
                    for (const column of columns) {
                        blocksWithConditional.push(...collectBlocksWithConditionalRendering(column));
                    }
                }
            }

            if (block.blocks) {
                blocksWithConditional.push(...collectBlocksWithConditionalRendering(block.blocks));
            }
        }

        return blocksWithConditional;
    };

    const updateConditionalRendering = useCallback(() => {
        if (!page?.blocks) return;

        const blocksWithConditional = collectBlocksWithConditionalRendering(page.blocks);

        blocksWithConditional.forEach((block) => {
            const blockElement = blockRefs.current[block.id];
            if (!blockElement) return;

            const { watchInputBlockId, watchValue } = block.data!.conditionalRendering!;
            const watchedValue = getInputValue(watchInputBlockId);
            const shouldShow = watchedValue === watchValue;

            blockElement.style.display = shouldShow ? "" : "none";
        });
    }, [page?.blocks]);

    useEffect(() => {
        if (page?.blocks) {
            setTimeout(() => {
                updateConditionalRendering();
            }, 100);
        }
    }, [page?.blocks, updateConditionalRendering]);

    const BlockRenderer = ({ block }: { block: Block }): React.ReactNode => {
        const blockRef = useRef<HTMLDivElement>(null);

        useEffect(() => {
            if (blockRef.current) {
                blockRefs.current[block.id] = blockRef.current;
                updateConditionalRendering();
            }
            return () => {
                delete blockRefs.current[block.id];
            };
        }, [block.id, updateConditionalRendering]);

        switch (block.type) {
            case "container":
                return (
                    <div ref={blockRef} data-block-id={block.id}>
                        <ContainerBlockComponent block={block} />
                    </div>
                );
            case "input":
                return (
                    <div ref={blockRef} data-block-id={block.id}>
                        <InputBlockComponent block={block} />
                    </div>
                );
            case "html":
                return (
                    <div ref={blockRef} data-block-id={block.id}>
                        <HTMLBlockComponent block={block} />
                    </div>
                );
            case "text":
                return (
                    <div ref={blockRef} data-block-id={block.id}>
                        <TextBlockComponent block={block} />
                    </div>
                );
            case "image":
                return (
                    <div ref={blockRef} data-block-id={block.id}>
                        <MediaBlockComponent block={block} />
                    </div>
                );
            case "button":
                return (
                    <div ref={blockRef} data-block-id={block.id}>
                        <ButtonBlockComponent block={block} />
                    </div>
                );
            default:
                return null;
        }
    };

    const renderBlocks = () => {
        if (!page.blocks || !Array.isArray(page.blocks) || page.blocks.length === 0) {
            return (
                <div className="text-center text-gray-500 py-12">
                    <p>No content available</p>
                </div>
            );
        }

        return (
            <div className="prose max-w-none">
                {page.blocks.map((block: Block) => (
                    <BlockRenderer key={block.id} block={block} />
                ))}
            </div>
        );
    };

    return (
        <div className="min-h-screen relative">
            {page.backgroundImageUrl && (
                <>
                    <div
                        className="absolute inset-0 -z-10"
                        style={{
                            backgroundImage: `url(${page.backgroundImageUrl})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            filter: 'blur(12px)',
                        }}
                    />
                    <div
                        className="absolute inset-0 -z-10"
                        style={{
                            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.5) 0%,  rgba(0, 0, 0, 1) 100%)',
                        }}
                    />
                </>
            )}
            {!page.backgroundImageUrl && (
                <div
                    className="absolute inset-0 -z-10"
                    style={{
                        backgroundColor: page.pageBackgroundColor || ""
                    }}
                />
            )}
            <div
                className="flex-1 overflow-y-auto flex justify-center relative z-0 pages-app-content"
                style={{
                    fontFamily: page.pageFontFamily || undefined,
                    color: page.pageTextColor || undefined
                }}
            >
                <div className="w-full">
                    {renderBlocks()}
                </div>
            </div>
            <CheckoutDialog
                open={checkoutDialogOpen}
                onOpenChange={setCheckoutDialogOpen}
            />
        </div>
    );
}
